import { defer, EMPTY, expand, map, Observable, reduce } from 'rxjs';
import { IPaginated } from '../types/http.interface';

/** Load a complete option list using the effective page size returned by the server. */
export function collectPages<T>(
  loadPage: (page: number, limit: number) => Observable<IPaginated<T[]>>,
): Observable<IPaginated<T[]>> {
  const load = (page: number, limit: number) => defer(() => loadPage(page, limit)).pipe(
    map(response => ({ response, page })),
  );
  return load(1, 100).pipe(
    expand(({ response, page }) => {
      if (!Number.isSafeInteger(response.meta.limit) || response.meta.limit <= 0
        || !Number.isSafeInteger(response.meta.total) || response.meta.total < 0
        || response.meta.current !== page) {
        throw new Error('The option list could not be loaded completely. Please try again.');
      }
      // Tolerant backend reads can omit malformed records after paging the cursor.
      // Advance using the server's page metadata even when a page has no valid rows.
      if (page * response.meta.limit >= response.meta.total) return EMPTY;
      return load(page + 1, response.meta.limit);
    }, 1),
    reduce((result, { response }) => ({
      items: [...result.items, ...response.items],
      meta: { ...response.meta, current: 1, limit: result.items.length + response.items.length },
    }), { items: [] as T[], meta: { total: 0, current: 1, limit: 0 } }),
  );
}
